"use client";

import {
  createContext,
  Fragment,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  CURRENCY_STORAGE_KEY,
  SUPPORTED_CURRENCIES,
  type Currency,
} from "./constants";

interface CurrencyContextType {
  currency: Currency;
  setCurrency: (currency: Currency) => void;
}

const DEFAULT_CURRENCY: Currency = "EUR";

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

const isCurrency = (value: string | null): value is Currency => {
  if (!value) return false;
  return SUPPORTED_CURRENCIES.includes(value as Currency);
};

interface CurrencyProviderProps {
  children: ReactNode;
}

export function CurrencyProvider({ children }: CurrencyProviderProps) {
  const [currency, setCurrencyState] = useState<Currency>(() => {
    if (typeof window === "undefined") return DEFAULT_CURRENCY;
    const stored = window.localStorage.getItem(CURRENCY_STORAGE_KEY);
    return isCurrency(stored) ? stored : DEFAULT_CURRENCY;
  });

  const setCurrency = useCallback((nextCurrency: Currency) => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(CURRENCY_STORAGE_KEY, nextCurrency);
    }
    setCurrencyState(nextCurrency);
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(CURRENCY_STORAGE_KEY, currency);
    }
  }, [currency]);

  const value = useMemo(
    () => ({
      currency,
      setCurrency,
    }),
    [currency, setCurrency]
  );

  return (
    <CurrencyContext.Provider value={value}>
      <Fragment key={currency}>{children}</Fragment>
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error("useCurrency must be used within a CurrencyProvider");
  }
  return context;
}
